/**
 * The catalogue — `@annwn/content`, in the shape `@annwn/sim` asks for.
 *
 * The sim depends on nothing, so it cannot import a wound table or a body plan;
 * what it takes instead is a `Catalogue` on its rule set, and this is the one
 * the live game runs. Everything here is a lookup into a kit already declared
 * elsewhere in this package — the file adds no content, only the seam.
 *
 * Lookups return `null` rather than throwing, deliberately. A world restored
 * against a content revision that dropped a weapon should degrade to a fighter
 * who cannot swing it, not take the server down on the tick they try.
 */
import { createAttributes } from '@annwn/sim';
import { ARMOUR_KIT } from './armour.js';
import { BACKGROUND_KIT } from './backgrounds.js';
import { VALUES } from './barter.js';
import { ERRAND_KIT, ERRANDS } from './errands.js';
import { ATTACK_PROFILES } from './attackProfiles.js';
import { BODY_PLANS } from './bodyPlans/index.js';
import { FLORA_KIT, ITEM_KIT } from './flora.js';
import { HERB_KIT, PREPARATIONS } from './herbs.js';
import { MARKS, MARK_KIT } from './marks.js';
import { RUMOURS } from './rumours.js';
import { VILLAGER_KIT } from './villagers.js';
import { NARRATION_POOLS } from './narration/index.js';
import { WEAPON_KIT } from './weapons.js';
import { BEAST_KIT, BUTCHERY, INSTINCT_KIT } from './wildlife.js';
import { WOUND_TABLES } from './woundTables/index.js';
/**
 * What somebody walks into Annwn as, before M6-01 gives them a character:
 * an ordinary person in wool, with their hands.
 *
 * Deliberately unarmed and unarmoured. The first playtest's question is whether
 * *being in a fight at all* reads as dangerous, and a starting kit that answered
 * it with a spear would be answering a different question.
 *
 * The tunic is not a contradiction: it is worth no armour value at all and
 * exists so that the log can name what a blade opened on its way in (M2-04's
 * clothing layer). Everybody in the valley is wearing one.
 */
export const DEFAULT_LOADOUT = {
    planId: 'human',
    weaponId: 'fists',
    armourIds: ['tunic'],
    attributes: createAttributes(),
    skill: 0,
};
export const CONTENT_CATALOGUE = {
    defaultLoadout: DEFAULT_LOADOUT,
    woundTable: WOUND_TABLES,
    narration: NARRATION_POOLS,
    plan: (id) => BODY_PLANS.get(id) ?? null,
    weapon: (id) => WEAPON_KIT.get(id) ?? null,
    profile: (id) => ATTACK_PROFILES.get(id) ?? null,
    armour: (id) => ARMOUR_KIT.get(id) ?? null,
    instinct: (id) => INSTINCT_KIT.get(id) ?? null,
    beast: (id) => BEAST_KIT.get(id) ?? null,
    item: (id) => ITEM_KIT.get(id) ?? null,
    // The two RC2 surfaces resolve their prose here: a background's trade and
    // history for the sheet's identity line (RC2-03), an errand's invitation for
    // its Journal. `background` hands back only the brief the sheet needs, not the
    // whole kit — the loadout is `seatBody`'s business, not the projection's.
    background: (id) => {
        const found = BACKGROUND_KIT.get(id);
        return found ? { id: found.id, trade: found.name, blurb: found.history } : null;
    },
    errand: (id) => ERRAND_KIT.get(id) ?? null,
    errands: () => ERRANDS,
    flora: (id) => FLORA_KIT.get(id) ?? null,
    herb: (id) => HERB_KIT.get(id) ?? null,
    preparations: () => PREPARATIONS,
    mark: (id) => MARK_KIT.get(id) ?? null,
    villager: (id) => VILLAGER_KIT.get(id) ?? null,
    marks: () => MARKS,
    butchery: (id) => BUTCHERY.get(id) ?? null,
    // M5-01's economy, and the whole of it: what a thing is worth in barter, and
    // the words the village tells its own news in. Both are tables — there is no
    // currency and there are no written rumours.
    value: (id) => VALUES.get(id) ?? 0,
    rumours: () => RUMOURS,
};
//# sourceMappingURL=catalogue.js.map